import { EventEmitter, Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TrackSearchCatalogParams } from "@app/models/tracking/track-event-param-type";
import { isNullish } from "@commonHelpers/math-utils";
import { RequestService } from "@commonServices/request.service";
import { CatalogSearchResultDisplayType } from "@enums/catalog-search-result-display-type";
import { CatalogItemSearchResultDto, CatalogOrCatalogItemSearchResultDto, CatalogOrCatalogItemSuggestionsDto, Client as SearchAPI } from "@interfaces/HttpClient/SearchApiModels";
import { CatalogSearchRequest, SearchRequest } from "@interfaces/SearchRequest";
import { Observable, Subject, firstValueFrom, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AnalyticsTrackingFacadeService } from "./analytics-tracking-facade.service";
import { SearchResultService } from "./search-result.service";
import { SearchbarService } from "./searchbar.service";

@Injectable()
export class SearchService {


	private inCatalogTeaserLength = 100;

	private refreshSearch = new Subject<any>();
	private static currentCatalogSearchRequest: CatalogSearchRequest;
	private static currentSearchRequest: SearchRequest;
	private static currentCatalogItemSearchResultDto: CatalogItemSearchResultDto;
	private static currentSearchResultDto: CatalogOrCatalogItemSearchResultDto;
	private static nextGlobalSearchCall: Subject<void> = new Subject<void>();
	private static nextCatalogSearchCall: Subject<void> = new Subject<void>();
	private static nextSearchSuggestionCall: Subject<void> = new Subject<void>();
	private static nextCatalogSearchSuggestionCall: Subject<void> = new Subject<void>();
	private static isSearching = false;
	private static searchEmmiter = new EventEmitter<void>();

	constructor(
		private searchApi: SearchAPI,
		private requestService: RequestService,
		private router: Router,
		private route: ActivatedRoute,
		private searchbarService: SearchbarService,
		private searchResultService: SearchResultService,
		protected trackingFacadeService?: AnalyticsTrackingFacadeService
	) { }

	get nextGlobalSearchCall(): Observable<void> {
		return SearchService.nextGlobalSearchCall.asObservable();
	}

	get nextCatalogSearchCall(): Observable<void> {
		return SearchService.nextCatalogSearchCall.asObservable();
	}

	async search(
		search: SearchRequest
	): Promise<CatalogOrCatalogItemSearchResultDto> {

		if (!search.searchTerm) {
			search.searchTerm = "";
		}
		try {
			if (search.searchTerm.trim().length === 0) {
				return null;
			}
			if (JSON.stringify(search) === JSON.stringify(SearchService.currentSearchRequest)) {
				return firstValueFrom(of(SearchService.currentSearchResultDto));
			} else {
				SearchService.nextGlobalSearchCall.next();
				SearchService.currentSearchRequest = search;
				const currentResult = await firstValueFrom(this.searchApi
					.search_SearchCatalogsOrCatalogItems(
						search.searchTerm,
						search.searchFilter,
						search.pageIndex,
						search.pageSize
					).pipe(takeUntil(SearchService.nextGlobalSearchCall)));
				if (currentResult === undefined) {
					return firstValueFrom(of(SearchService.currentSearchResultDto));
				}

				if (currentResult?.totalCatalogItemHitsCount > 0) {
					this.searchbarService.lastSearchResultTerm = search.searchTerm;
				}
				SearchService.currentSearchResultDto = currentResult;
				return currentResult;
			}
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async navigateToCatalog(catalogSearchRequest: CatalogSearchRequest, searchDisplayType?: CatalogSearchResultDisplayType) {
		const searchResult = await this.searchInsidePublicCatalog(catalogSearchRequest);
		if (!isNullish(searchResult)) {
			if (!searchResult.indexDirty && !searchResult?.totalCount) {
				this.searchResultService.catalogTriggerSearchNoResultTrigger.emit({ searchResult, searchTerm: catalogSearchRequest.searchTerm, refresh: false, catalogKey: catalogSearchRequest.catalogKey })
			} else {
				this.searchbarService.insertSearchInput(catalogSearchRequest.searchTerm);
				this.searchbarService.searchTerm = catalogSearchRequest?.searchTerm ?? "";
				const route = ['catalog', catalogSearchRequest.catalogKey];
				if (searchResult?.hits.length > 0) {
					route.push("catalogItem");
					route.push(searchResult.hits.find(f => f !== undefined)?.guid);
				}

				route.push("search");

				const searchFilter = catalogSearchRequest.searchFilter !== "" ? catalogSearchRequest.searchFilter : undefined;
				const displayType = searchDisplayType ?? CatalogSearchResultDisplayType.Catalog;
				await this.router.navigate(route, { queryParams: { searchTerm: catalogSearchRequest.searchTerm, searchPage: 1, searchFilter, searchResultDisplayType: displayType, mobileView: this.route.snapshot.queryParams.mobileView } });
			}
		}
	}

	async searchInsidePublicCatalog(catalogSearchRequest: CatalogSearchRequest
	): Promise<CatalogItemSearchResultDto> {
		if (isNullish(catalogSearchRequest.searchTerm)) {
			catalogSearchRequest.searchTerm = "";
		}
		if (isNullish(catalogSearchRequest.searchFilter)) {
			catalogSearchRequest.searchFilter = "";
		}
		try {
			if (JSON.stringify(catalogSearchRequest) === JSON.stringify(SearchService.currentCatalogSearchRequest)) {
				if (SearchService.isSearching) {
					await firstValueFrom(SearchService.searchEmmiter)
				}
				return firstValueFrom(of(SearchService.currentCatalogItemSearchResultDto));
			} else {
				SearchService.currentCatalogSearchRequest = catalogSearchRequest;
				SearchService.nextCatalogSearchCall.next();
				SearchService.isSearching = true;
				const currentResult = await firstValueFrom(this.searchApi
					.search_SearchCatalogItems(
						catalogSearchRequest.catalogKey,
						catalogSearchRequest.searchTerm,
						catalogSearchRequest.searchFilter,
						catalogSearchRequest.pageIndex,
						catalogSearchRequest.pageSize,
						this.inCatalogTeaserLength
					).pipe(takeUntil(SearchService.nextCatalogSearchCall)));
				this.trackingFacadeService.handleTrackEvent(new TrackSearchCatalogParams(catalogSearchRequest, currentResult?.totalCount ?? 0))
				if (currentResult === undefined) {
					this.completeSearch();
					return firstValueFrom(of(SearchService.currentCatalogItemSearchResultDto));
				}

				SearchService.currentCatalogItemSearchResultDto = currentResult;
				SearchService.currentCatalogSearchRequest = catalogSearchRequest;
				this.completeSearch();
				return SearchService.currentCatalogItemSearchResultDto;
			}
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined },
				{ status: 504, return: undefined }
			]);
		} finally {
			if (SearchService?.currentCatalogItemSearchResultDto?.totalCount > 0 || SearchService?.currentCatalogItemSearchResultDto?.indexDirty) {
				this.searchbarService.insertSearchInput(catalogSearchRequest.searchTerm);
				this.searchbarService.searchTerm = catalogSearchRequest?.searchTerm ?? "";
				this.searchbarService.lastSearchResultTerm = catalogSearchRequest.searchTerm;
				this.completeSearch();
			}
		}
	}

	private completeSearch() {
		SearchService.isSearching = false;
		SearchService.searchEmmiter.next(null)
		SearchService.searchEmmiter.complete();
		SearchService.searchEmmiter = new EventEmitter();
	}

	refresh(): void {
		this.refreshSearch.next(null);
	}

	getRefreshHandler(): Observable<any> {
		return this.refreshSearch.asObservable();
	}

	async getCatalogSuggestions(
		catalogKey: string,
		searchTerm: string | null

	): Promise<CatalogOrCatalogItemSuggestionsDto> {
		SearchService.nextSearchSuggestionCall.next(null);
		// Wegen internen rxjs fehler Uncaught (in promise): EmptyError: no elements in sequence wird hier die Ausgabe manuell mit Promise gemacht
		return new Promise((resolve, reject) => {
			const observable = this.searchApi
				.search_SuggestTerms(
					catalogKey,
					searchTerm
				).pipe(takeUntil(SearchService.nextSearchSuggestionCall));
			observable.subscribe(result => resolve(result));
		});
	}

	async getSuggestions(
		searchTerm: string, categoryGroupId?: number | null | undefined
	): Promise<CatalogOrCatalogItemSuggestionsDto> {
		SearchService.nextSearchSuggestionCall.next(null);

		// Wegen internen rxjs fehler Uncaught (in promise): EmptyError: no elements in sequence wird hier die Ausgabe manuell mit Promise gemacht
		return new Promise((resolve, reject) => {
			const observable = this.searchApi
				.search_SuggestTermsAndCatalogs(
					searchTerm,
					categoryGroupId
				).pipe(takeUntil(SearchService.nextSearchSuggestionCall));
			observable.subscribe(result => resolve(result));
		});
	}

	async didYouMean(searchTerm: string | null, catalogKey?: string | null | undefined, isPreview?: boolean, categoryGroupId?: number | null | undefined): Promise<string[]> {
		if (!searchTerm) {
			searchTerm = "";
		}
		try {
			const result = await firstValueFrom(this.searchApi.search_DidYouMean(searchTerm, catalogKey, isPreview, categoryGroupId));
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

}
